import React, { useEffect, useState } from 'react';
import { X, Play, Plus, Check, Star, Calendar, ExternalLink } from 'lucide-react';
import { imgUrl, getDetail, getTrailerKey, getWatchProviders } from '../api/tmdb';
import { useDetail } from '../context/DetailContext';
import { useApp } from '../context/AppContext';
import { useSubscription } from '../context/SubscriptionContext';
import useYouTubeProgress from '../hooks/useYouTubeProgress';
import { affiliateOutUrl } from '../api/services';
import Paywall from './Paywall';
import MovieCard from './MovieCard';

const DetailModal = () => {
  const { item, close } = useDetail();
  const { isInList, toggleMyList, trackOpen } = useApp();
  const { isSubscribed } = useSubscription();
  const [detail, setDetail] = useState(null);
  const [trailerKey, setTrailerKey] = useState(null);
  const [providers, setProviders] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [paywall, setPaywall] = useState(false);

  useEffect(() => {
    setDetail(null);
    setTrailerKey(null);
    setProviders(null);
    setPlaying(false);
    if (item) {
      const mt = item.media_type || (item.first_air_date ? 'tv' : 'movie');
      getDetail(mt, item.id).then(setDetail).catch(() => {});
      getTrailerKey(mt, item.id).then(setTrailerKey);
      getWatchProviders(mt, item.id).then(setProviders).catch(() => {});
      trackOpen(item);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    if (item) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [item, close]);

  const title = item?.title || item?.name;
  const year = (item?.release_date || item?.first_air_date || '').slice(0, 4);
  const inList = isInList(item);
  const similar = detail?.similar?.results?.filter((s) => s.poster_path || s.backdrop_path) || [];
  const mediaType = item?.media_type || (item?.first_air_date ? 'tv' : 'movie');

  // Progress tracking on trailer playback (hook must run every render)
  useYouTubeProgress(
    playing && trailerKey && item ? `dm-yt-${item.id}` : null,
    item ? {
      tmdb_id: item.id,
      media_type: mediaType,
      title,
      poster_path: item.poster_path,
      backdrop_path: item.backdrop_path,
      youtube_id: trailerKey,
    } : null,
    !!(playing && trailerKey && item),
  );

  if (!item) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 md:p-10 overflow-y-auto bg-black/80 backdrop-blur-sm">
      <div className="modal-in relative w-full max-w-5xl bg-[#0e0e14] rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_100px_rgba(0,0,0,0.8)]">
        <button
          onClick={close}
          className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-black/70 hover:bg-black text-white flex items-center justify-center tv-focusable"
        >
          <X size={20} />
        </button>

        {/* Hero area */}
        <div className="relative aspect-video w-full bg-black">
          {playing && trailerKey ? (
            <iframe
              id={`dm-yt-${item.id}`}
              title="trailer"
              className="absolute inset-0 w-full h-full"
              src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`}
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <>
              <img
                src={imgUrl(item.backdrop_path || item.poster_path, 'original')}
                alt={title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0e0e14] via-[#0e0e14]/40 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8 md:p-10 max-w-2xl">
                <h2 className="font-display text-4xl md:text-6xl text-white leading-none">{title}</h2>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    onClick={() => {
                      if (!trailerKey) return;
                      setPlaying(true);
                    }}
                    disabled={!trailerKey}
                    className="tv-focusable inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-black font-semibold hover:bg-white/90 disabled:opacity-60"
                  >
                    <Play size={18} className="fill-black" /> {trailerKey ? 'Watch Trailer' : 'No Trailer'}
                  </button>
                  <button
                    onClick={() => toggleMyList(item)}
                    className="tv-focusable inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-white/15 text-white hover:bg-white/25 backdrop-blur"
                  >
                    {inList ? <Check size={18} /> : <Plus size={18} />} {inList ? 'In My List' : 'Add to List'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Info */}
        <div className="p-8 md:p-10 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-4">
            <div className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm text-white/70 flex items-start gap-2">
              <span className="text-[#E50914] mt-0.5">ℹ</span>
              <div>
                <span className="text-white font-medium">Trailer & info only.</span> BLUNT TV is a discovery platform — full playback of this title is available on the studio&apos;s own streaming service. For exclusive full-length films and series, explore <span className="text-white font-medium">BLUNT Originals</span>.
              </div>
            </div>

            {providers && (providers.flatrate.length > 0 || providers.rent.length > 0 || providers.buy.length > 0 || providers.free.length > 0 || providers.ads.length > 0) && (
              <div className="px-4 py-4 rounded-xl bg-gradient-to-br from-[#E50914]/8 via-white/5 to-transparent border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <ExternalLink size={14} className="text-[#E50914]" />
                    <span className="text-white font-semibold text-sm">Where to watch the full title</span>
                  </div>
                  {providers.link && (
                    <a href={affiliateOutUrl(providers.link, 'justwatch', title)} target="_blank" rel="noreferrer" className="text-[#E50914] hover:underline text-xs font-medium">
                      View all on JustWatch →
                    </a>
                  )}
                </div>

                {providers.flatrate.length > 0 && (
                  <ProviderGroup label="Stream" providers={providers.flatrate} link={providers.link} title={title} />
                )}
                {providers.free.length > 0 && (
                  <ProviderGroup label="Free" providers={providers.free} link={providers.link} title={title} />
                )}
                {providers.ads.length > 0 && (
                  <ProviderGroup label="Free with ads" providers={providers.ads} link={providers.link} title={title} />
                )}
                {providers.rent.length > 0 && (
                  <ProviderGroup label="Rent" providers={providers.rent} link={providers.link} title={title} />
                )}
                {providers.buy.length > 0 && (
                  <ProviderGroup label="Buy" providers={providers.buy} link={providers.link} title={title} />
                )}

                <div className="text-[11px] text-white/40 mt-3">
                  Availability via JustWatch • US region • pricing set by each provider
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
              {item.vote_average ? (
                <span className="flex items-center gap-1"><Star size={14} className="text-amber-400 fill-amber-400" /> {item.vote_average.toFixed(1)}</span>
              ) : null}
              {year && <span className="flex items-center gap-1"><Calendar size={14} /> {year}</span>}
              {detail?.runtime ? <span>{detail.runtime} min</span> : null}
              {detail?.number_of_seasons ? <span>{detail.number_of_seasons} Season{detail.number_of_seasons > 1 ? 's' : ''}</span> : null}
              <span className="px-2 py-0.5 rounded border border-white/20 text-[10px] tracking-wider">HD</span>
            </div>
            <p className="text-white/85 leading-relaxed text-[15px]">{item.overview}</p>
            {detail?.credits?.cast?.length > 0 && (
              <div className="text-sm">
                <span className="text-white/50">Cast: </span>
                <span className="text-white/85">{detail.credits.cast.slice(0, 5).map((c) => c.name).join(', ')}</span>
              </div>
            )}
            {detail?.genres?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {detail.genres.map((g) => (
                  <span key={g.id} className="px-3 py-1 rounded-full bg-white/8 border border-white/10 text-xs">{g.name}</span>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-3 text-sm">
            {detail?.production_companies?.length > 0 && (
              <div>
                <div className="text-white/50 mb-1">Studio</div>
                <div className="text-white/85">{detail.production_companies.slice(0, 2).map((p) => p.name).join(', ')}</div>
              </div>
            )}
            {detail?.original_language && (
              <div>
                <div className="text-white/50 mb-1">Language</div>
                <div className="text-white/85 uppercase">{detail.original_language}</div>
              </div>
            )}
            {detail?.status && (
              <div>
                <div className="text-white/50 mb-1">Status</div>
                <div className="text-white/85">{detail.status}</div>
              </div>
            )}
          </div>
        </div>

        {similar.length > 0 && (
          <div className="pb-10">
            <h3 className="px-8 md:px-10 text-lg font-semibold mb-4">More Like This</h3>
            <div className="row-scroll flex gap-4 px-8 md:px-10 py-4 overflow-x-auto">
              {similar.slice(0, 12).map((s) => (
                <MovieCard key={s.id} item={s} />
              ))}
            </div>
          </div>
        )}
      </div>
      <Paywall open={paywall} onClose={() => setPaywall(false)} />
    </div>
  );
};

export default DetailModal;

const ProviderGroup = ({ label, providers, link, title }) => {
  const providerKey = (name = '') => name.toLowerCase().split(' ')[0];
  return (
    <div className="mb-3 last:mb-0">
      <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {providers.map((p) => (
          <a
            key={`${label}-${p.provider_id}`}
            href={affiliateOutUrl(link || '#', providerKey(p.provider_name), title || '')}
            target="_blank"
            rel="noreferrer sponsored"
            title={`Watch on ${p.provider_name}`}
            className="tv-focusable flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/10 transition"
          >
            {p.logo_path && (
              <img
                src={`https://image.tmdb.org/t/p/w45${p.logo_path}`}
                alt={p.provider_name}
                className="w-6 h-6 rounded object-cover"
              />
            )}
            <span className="text-white text-xs font-medium pr-1">{p.provider_name}</span>
          </a>
        ))}
      </div>
    </div>
  );
};
